import type { ChangeEvent, InputHTMLAttributes, JSX, ReactElement } from "react";
import { Fragment, useCallback, useEffect, useId, useState } from "react";
import * as E from "fp-ts/lib/Either";
import * as Eq from "fp-ts/lib/Eq";
import { constFalse, identity, pipe } from "fp-ts/lib/function";
import * as O from "fp-ts/lib/Option";
import * as RA from "fp-ts/lib/ReadonlyArray";
import type { ReadonlyNonEmptyArray } from "fp-ts/lib/ReadonlyNonEmptyArray";
import * as RNEA from "fp-ts/lib/ReadonlyNonEmptyArray";

import { gtagClickEvent } from "@scripts/analytics";
import type { BLConfigWithLog } from "@scripts/bondlink";
import type { Describe } from "@scripts/fp-ts/lib/types";
import type { Primitive } from "@scripts/fp-ts/lib/types/matchers";
import type { BaseInputFieldProps, BaseInputProps } from "@scripts/react/components/form/Form";
import { useConfig } from "@scripts/react/context/Config";
import type { Codec } from "@scripts/react/form/codecs";
import type { DataCodec, StateProps, UnsafeFormProp, ValErrMsgA, ValErrMsgAR, ValidationErrors } from "@scripts/react/form/form";
import { clearAndSetState, getValErr, updateAndSetState, useUpdateAndSetStateViaCodecTransition } from "@scripts/react/form/form";
import { reactChildToStringStripTagsE, requiredProps as requireProps } from "@scripts/react/syntax/react";
import type { KlassProp } from "@scripts/react/util/classnames";
import { klass, klassConditional } from "@scripts/react/util/classnames";
import type { ExhaustivePickO } from "@scripts/types/exhaustivePick";
import type { RequireUndefinableKeys } from "@scripts/types/requireUndefinableKeys";
import { eq } from "@scripts/util/eq";
import { exhaustiveNoLog } from "@scripts/util/exhaustive";
import type { NonArray } from "@scripts/util/nonArray";

import { ButtonLink } from "../Button";
import { constEmpty, Empty, mapOrEmpty, type ReactElementOrEmpty, toEmpty } from "../Empty";
import { disabledClass, errorClass, LabelElBase, valErrMsgEls } from "./Labels";

type IndicatorLabel = string | ReactElement;
type AriaLabel = { "aria-label": string };
type AriaLabelledBy = { "aria-labelledby": string };
type AriaU = AriaLabel | AriaLabelledBy;
type LabelOrAria = IndicatorLabel | AriaU;

const isAriaLabel = (l?: LabelOrAria): l is AriaLabel => typeof l === "object" && "aria-label" in l;
const isAriaLabelledBy = (l?: LabelOrAria): l is AriaLabelledBy => typeof l === "object" && "aria-labelledby" in l;
const isAria = (l?: LabelOrAria): l is AriaU => isAriaLabel(l) || isAriaLabelledBy(l);
const isLabel = (l?: LabelOrAria): l is IndicatorLabel => !isAria(l);

const labelFold = <A,>(
  l: LabelOrAria,
  label: (label: IndicatorLabel) => A,
  ariaLabel: (ariaLabel: string) => A,
  ariaLabelledBy: (ariaLablledBy: string) => A
): A => {
  if (isLabel(l)) {
    return label(l);
  } else if (isAriaLabel(l)) {
    return ariaLabel(l["aria-label"]);
  } else if (isAriaLabelledBy(l)) {
    return ariaLabelledBy(l["aria-labelledby"]);
  }

  return exhaustiveNoLog(l);
};

export type IndicatorOption<KV, L extends LabelOrAria = LabelOrAria> = {
  label: L;
  value: KV;
  description?: string;
  disabled?: boolean;
};

const checkboxCompare = <KV extends string | number>(optionValue: KV) =>
  O.fold(
    () => false,
    RA.exists(eq(optionValue))
  );

type IndicatorType = "checkbox" | "radio";

export type IndicatorInputRawProps = Describe<
  Pick<InputHTMLAttributes<HTMLInputElement>, "onChange" | "tabIndex" | "value" | "disabled">
  & {
    name: string;
    label: LabelOrAria;
    description?: string | undefined;
    tooltip?: ReactElement | undefined;
    checked: boolean;
    labelKlass?: KlassProp | undefined;
    type: IndicatorType;
  }
>;

export const IndicatorInputRaw = (props: IndicatorInputRawProps) => {
  return (
    <label {...klass("custom-indicator", disabledClass(props.disabled), props.labelKlass)}>
      <input
        {...(isAria(props.label) ? props.label : {})}
        checked={props.checked}
        disabled={props.disabled}
        name={props.name}
        onChange={props.onChange}
        tabIndex={props.tabIndex}
        type={props.type}
        value={props.value}
      />
      {isLabel(props.label) && <span {...klass("option-label")}>{props.label}{props.tooltip}</span>}
      {props.description && <span {...klass("custom-indicator-description")}>{props.description}</span>}
      <div {...klass("indicator")} />
    </label>
  );
};

const IndicatorInputRawStrict = requireProps(IndicatorInputRaw);


export type IndicatorKV = string | number;

type IndicatorInputBase = Describe<
  ExhaustivePickO<IndicatorInputRawProps, {
    onChange: false;
    tabIndex: true;
    value: false;
    name: true;
    label: false;
    description: false;
    tooltip: true;
    checked: false;
    labelKlass: false;
    type: false;
    disabled: true;
  }>
>;

type IndicatorInputBaseProps<KV extends IndicatorKV> =
  IndicatorInputBase
  & ({
    type: "radio";
    onChange: (e: KV) => void;
    value: O.Option<KV>;
  } | {
    type: "checkbox";
    onChange: (e: ReadonlyArray<KV>) => void;
    value: O.Option<ReadonlyArray<KV>>;
  })
  ;

const IndicatorInputBase = <KV extends IndicatorKV>(props: RequireUndefinableKeys<IndicatorInputBaseProps<KV> & { option: IndicatorOption<KV> }>) => {
  const checked = props.type === "checkbox" ? checkboxCompare(props.option.value)(props.value) : O.exists(eq(props.option.value))(props.value);

  const onChange = useCallback(() => {
    if (!checked) {
      props.type === "checkbox"
        ? props.onChange(Array.from(new Set(O.getOrElse(RA.zero<KV>)(props.value)).add(props.option.value)))
        : props.onChange(props.option.value);
    } else {
      props.type === "checkbox" && props.onChange(O.getOrElse(RA.zero<KV>)(props.value).filter(_ => _ !== props.option.value));
    }
  }, [checked, props]);

  const isCustomInputDisabled = props.option.disabled ?? props.disabled ?? false;

  return <IndicatorInputRawStrict
    disabled={isCustomInputDisabled}
    label={props.option.label}
    description={props.option.description}
    labelKlass={klassConditional("checked")(checked)}
    name={props.name}
    type={props.type}
    value={props.option.value}
    checked={checked}
    onChange={onChange}
    tooltip={props.tooltip}
    tabIndex={props.tabIndex}
  />;
};


export type IndicatorGroupRawProps<KV extends IndicatorKV> =
  IndicatorInputBaseProps<KV> & {
    options: ReadonlyNonEmptyArray<IndicatorOption<KV>>;
    errors: ValErrMsgAR<UnsafeFormProp<KV> | UnsafeFormProp<KV[]>>;
    disabled?: boolean;
    hideErrorMessage?: true;
    klass?: KlassProp;
    aria: AriaU;
  };

export function IndicatorGroupRaw<KV extends IndicatorKV>(props: IndicatorGroupRawProps<KV>): ReactElement {
  return (
    <fieldset {...klass("form-input", errorClass(props.errors), disabledClass(props.disabled), props.klass)} {...props.aria}>
      {props.options.map((o: IndicatorOption<KV>) => <IndicatorInputBase key={`${o.value}`} {...props} option={o} disabled={props.disabled} tabIndex={props.tabIndex} tooltip={props.tooltip} />)}
      {!props.hideErrorMessage && valErrMsgEls(O.some(props.name), O.none)(props.errors)}
    </fieldset>
  );
}

type IndicatorGroupBaseProps<KV> = ExhaustivePickO<IndicatorInputRawProps, {
  onChange: false;
  tabIndex: true;
  value: false;
  disabled: true;
  name: true;
  label: false;
  description: false;
  tooltip: true;
  checked: false;
  labelKlass: true;
  type: true;
}> & {
  checked: (o: IndicatorOption<KV>) => boolean;
  onChange: (o: IndicatorOption<KV>, e: ChangeEvent<HTMLInputElement>) => void;
  valErrNode: () => ReactElementOrEmpty;
  veErr: ValErrMsgAR<UnsafeFormProp<KV> | UnsafeFormProp<KV[]>>;
  onClear?: () => void;
  codec: Codec<KV, UnsafeFormProp<KV> | null>;
  options: ReadonlyArray<IndicatorOption<KV>>;
  aria: AriaU;
};

function IndicatorGroupBase<KV>(props: RequireUndefinableKeys<IndicatorGroupBaseProps<KV>>): ReactElement {
  const optionsRNEA = RNEA.fromReadonlyArray(props.options);
  const isAnyOptionSelected = pipe(optionsRNEA, O.fold(
    constFalse,
    RA.exists(_ => props.checked(_)))
  );
  return <>{pipe(
    optionsRNEA,
    mapOrEmpty(options =>
      <fieldset
        {...props.aria}
        {...klass("form-input", errorClass(props.veErr), disabledClass(props.disabled))}
      >
        {options.map((o, idx) => {
          // eslint-disable-next-line react/no-array-index-key
          return <Fragment key={idx.toString()}>
            <div {...klass("d-flex")}>
              <IndicatorInputRawStrict
                label={o.label}
                labelKlass={props.labelKlass}
                name={props.name}
                type={props.type}
                value={O.getOrElse(() => "")(props.codec.encode(o.value))}
                checked={props.checked(o)}
                description={o.description}
                // eslint-disable-next-line no-undefined
                tooltip={undefined}
                // eslint-disable-next-line react/jsx-no-bind
                onChange={e => props.onChange(o, e)}
                tabIndex={props.tabIndex}
                disabled={props.disabled || o.disabled}
              />
              {toEmpty(O.fromNullable(props.tooltip))}
            </div>
          </Fragment>;
        })}
        {pipe(props.onClear, O.fromNullable, mapOrEmpty((c) => <ButtonLink klass={klassConditional("v-hidden")(!isAnyOptionSelected)} onClick={c}>Clear</ButtonLink>))}
        {props.valErrNode()}
      </fieldset>
    )
  )}</>;
}

const handleAnalytics = (config: BLConfigWithLog, analyticsScope: O.Option<string>, label: LabelOrAria) => (checked: boolean) => {
  const l = labelFold(
    label,
    _ => pipe(reactChildToStringStripTagsE(config, _), E.getOrElse(() => "no label")),
    identity,
    identity
  );
  return pipe(analyticsScope,
    O.map(scope =>
      gtagClickEvent("checkbox")(
        `${scope} ${l} ${checked.toString()}`
      )
    )
  );
};

type CustomIndicatorPropsBase<KV, InnerKV = KV> =
  ExhaustivePickO<
    IndicatorGroupBaseProps<InnerKV>, {
      tabIndex: true;
      disabled: true;
      name: true;
      tooltip: true;
      labelKlass: true;
      type: true;
      checked: false;
      onChange: false;
      valErrNode: false;
      veErr: false;
      onClear: true;
      codec: false;
      options: true;
      aria: true;
    }> & {
      analyticsScope: O.Option<string>;
    };

export type IndicatorProps<PC extends DataCodec, KV, InnerKV = KV> =
  Omit<BaseInputProps<PC, KV>, "requiredOverride"> & Omit<CustomIndicatorPropsBase<InnerKV>, "aria"> & {
    groupLabel?: LabelOrAria;
    required?: boolean;
    isClearable?: boolean;
    unselectedValue: O.Option<InnerKV>;
  };

export function Indicator<PC extends DataCodec, InnerKV>(
  props: IndicatorProps<PC, O.Option<NonArray<InnerKV>>, NonArray<InnerKV>>
): JSX.Element;
export function Indicator<PC extends DataCodec, KV>(props: IndicatorProps<PC, NonArray<KV>>): JSX.Element;
export function Indicator<PC extends DataCodec, KV>(props: IndicatorProps<PC, NonArray<KV>>): ReactElement {
  const config = useConfig();
  const [ve, dispatchVe] = useState(getValErr(props.state, props.lens)(config));
  useEffect(() => dispatchVe(getValErr(props.state, props.lens)(config)), [config, props.state, props.lens]);

  const id = useId();
  const labelString = pipe(
    props.groupLabel,
    O.fromNullable,
    O.chain(gl => labelFold(
      gl,
      _ => typeof _ === "string" ? O.some(_) : O.none,
      O.some,
      O.some,
    ))
  );
  const hasLabel = !props.groupLabel;

  const [localValue, updateStateTransition] = useUpdateAndSetStateViaCodecTransition<NonArray<KV>>(ve.val, props.codec.eq);
  const onChange = useCallback((o: IndicatorOption<NonArray<KV>>, e: ChangeEvent<HTMLInputElement>) => {
    handleAnalytics(config, props.analyticsScope, o.label)(e.target.checked);
    return e.target.checked
      ? updateStateTransition(props.setState, props.lens, props.codec)(o.value)
      : pipe(
        props.unselectedValue,
        O.fold(
          () => clearAndSetState(props.setState, props.lens),
          updateStateTransition(props.setState, props.lens, props.codec)
        )
      );
  }, [config, updateStateTransition, props.analyticsScope, props.codec, props.lens, props.setState, props.unselectedValue]);

  const isChecked = useCallback((o: IndicatorOption<NonArray<KV>>) => {
    return O.exists((lv: NonArray<KV>) => props.codec.eq.equals(o.value, lv))(localValue);
  }, [localValue, props.codec.eq]);

  const valErrNode = useCallback(() => valErrMsgEls(labelString, pipe(ve.val, O.chain(props.codec.encode)))(ve.err), [ve, props.codec.encode, labelString]);

  const onClear = useCallback(
    () => clearAndSetState(props.setState, props.lens),
    [props.setState, props.lens]
  );

  const labelEl = isLabel(props.groupLabel)
    ? typeof props.groupLabel === "string"
      ? <LabelElBase
        label={props.groupLabel}
        idOrFor={E.right(id)}
        required={props.required ?? false}
        klass={"font-size-default"}
      />
      : props.groupLabel
    : <Empty />;

  return <div {...klass("custom-indicator-wrapper", disabledClass(props.disabled), errorClass(ve.err), O.none)}>
    <div {...klass("custom-indicator-label")}>
      {labelEl}
      {valErrNode()}
    </div>
    <IndicatorGroupBase
      labelKlass={props.labelKlass}
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      aria={isAria(props.groupLabel) ? props.groupLabel : {} as AriaU}
      name={props.name}
      type={props.type}
      options={props.options}
      codec={props.codec}
      checked={isChecked}
      onChange={onChange}
      valErrNode={hasLabel ? constEmpty : valErrNode}
      veErr={hasLabel ? E.right<ValidationErrors<NonArray<KV>>, ValErrMsgA>([]) : ve.err}
      tabIndex={props.tabIndex}
      tooltip={props.tooltip}
      disabled={props.disabled}
      onClear={pipe(onClear, O.fromPredicate(() => props.isClearable ?? false), O.toUndefined)}
    />
  </div>;
}

type IndicatorGroupProps<PC extends DataCodec, KV extends Primitive> = Describe<
  BaseInputFieldProps<KV>
  & CustomIndicatorPropsBase<KV>
  & StateProps<PC, ReadonlyArray<KV>>
>;

export function IndicatorGroup<PC extends DataCodec, KV extends Primitive>(props: Describe<IndicatorGroupProps<PC, KV>>): ReactElement {
  const config = useConfig();
  const [ve, dispatchVe] = useState(getValErr(props.state, props.lens)(config));
  useEffect(() => dispatchVe(getValErr(props.state, props.lens)(config)), [config, props.state, props.lens]);

  const isChecked = useCallback((o: IndicatorOption<KV>) => pipe(ve.val, O.map(RA.exists(eq(o.value))), O.getOrElse(() => false)), [ve]);
  const onChange = useCallback((o: IndicatorOption<KV>, e: ChangeEvent<HTMLInputElement>) => {
    handleAnalytics(config, props.analyticsScope, o.label)(e.target.checked);
    pipe(
      ve.val,
      O.fold(
        () => e.target.checked ? O.some([o.value]) : O.none,
        v => O.some(e.target.checked ? RA.append(o.value)(v) : RA.filter(i => i !== o.value)(v))
      ),
      O.filter(RA.isNonEmpty),
      O.map(RA.uniq<KV>(Eq.eqStrict)),
      O.fold(
        () => updateAndSetState(props.setState, props.lens)([]),
        updateAndSetState(props.setState, props.lens)
      )
    );
  }, [config, props.analyticsScope, props.lens, props.setState, ve]);

  const valErrNode = useCallback(() =>
    valErrMsgEls(
      isAriaLabel(props.aria) ? O.some(props.aria["aria-label"]) : O.none,
      pipe(
        ve.val,
        O.filter(RA.isNonEmpty),
        O.chain(RA.traverse(O.Applicative)(props.codec.encode)),
        O.map(v => v.join(", "))
      )
    )(ve.err),
    [ve, props.codec.encode, props.aria]
  );

  return <IndicatorGroupBase
    labelKlass={O.none}
    name={props.name}
    type={props.type}
    options={props.options}
    codec={props.codec}
    checked={isChecked}
    onChange={onChange}
    valErrNode={valErrNode}
    veErr={ve.err}
    aria={props.aria}
    tooltip={props.tooltip}
    disabled={props.disabled}
    tabIndex={props.tabIndex}
    // eslint-disable-next-line no-undefined
    onClear={undefined}
  />;
}
